// =============================================
// AgreementApprovalPage
// עמוד אישור הסכם — מוצג למשתמשים שצריכים לחתום/לחדש הסכם
// =============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileCheck, ScrollText, CheckCircle, AlertCircle, Loader2, LogOut } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE_URL } from '../../services/api';

interface AgreementToSign {
  type: number;
  version: string;
  title: string;
  content: string;
  required: boolean;
  alreadySigned: boolean;
  signedAt: string | null;
}

export default function AgreementApprovalPage() {
  const navigate = useNavigate();
  const { token, user, setRequiresAgreement, logout } = useAuthStore();
  const [agreements, setAgreements] = useState<AgreementToSign[]>([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/agreements/to-sign`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.data) {
        setAgreements(data.data);
      } else {
        setError('שגיאה בטעינת ההסכם');
      }
    } catch {
      setError('שגיאה בטעינת ההסכם');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // 50px threshold
      if (scrollHeight - scrollTop - clientHeight < 50) {
        setScrolledToBottom(true);
      }
    }
  };

  const handleSign = async () => {
    if (!accepted) return;
    setSigning(true);
    setError('');

    try {
      // Sign all required (unsigned) agreements
      const unsignedRequired = agreements.filter(a => a.required && !a.alreadySigned);
      for (const agreement of unsignedRequired) {
        const res = await fetch(`${API_BASE_URL}/agreements/sign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agreementType: agreement.type,
            version: agreement.version,
          }),
        });

        const data = await res.json();
        if (!data.success) {
          setError(data.message || 'שגיאה בחתימה על ההסכם');
          setSigning(false);
          return;
        }
      }

      // All agreements signed successfully
      setSuccess(true);
      setRequiresAgreement(false);

      // Redirect after short delay
      setTimeout(() => {
        const role = user?.role;
        switch (role) {
          case 'parent':
            navigate('/parent', { replace: true });
            break;
          case 'tablet':
          case 'staff':
            navigate('/tablet', { replace: true });
            break;
          case 'teacher':
            navigate('/teacher', { replace: true });
            break;
          case 'admin':
            // מוקד חרום — serverRole CallCenter ינותב למוקד
            if (user?.serverRole === 'CallCenter') {
              navigate('/callcenter/dashboard', { replace: true });
            } else {
              navigate('/admin', { replace: true });
            }
            break;
          case 'sysadmin':
            navigate('/sysadmin', { replace: true });
            break;
          default:
            navigate('/', { replace: true });
        }
      }, 1500);
    } catch {
      setError('שגיאה בחתימה על ההסכם');
    } finally {
      setSigning(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  // Combine all required agreement content into one view
  const requiredAgreements = agreements.filter(a => a.required && !a.alreadySigned);
  const allContent = requiredAgreements
    .map(a => `# ${a.title}\n\n${a.content}`)
    .join('\n\n---\n\n');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50/60 via-white to-teal-50/40 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50/60 via-white to-teal-50/40" dir="rtl">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-teal-100 mb-4">
            <ScrollText className="w-7 h-7 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">אישור תנאי שימוש</h1>
          <p className="text-gray-500 text-sm">
            {user?.firstName ? `שלום ${user.firstName}, ` : ''}
            יש לקרוא ולאשר את תנאי השימוש כדי להמשיך לשירות
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-sm border border-green-200 p-8 text-center"
            >
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">ההסכם נחתם בהצלחה!</h2>
              <p className="text-gray-500 text-sm">מעביר אותך למערכת...</p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Agreement Content */}
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="p-6 overflow-y-auto prose prose-sm prose-gray max-w-none"
                style={{ maxHeight: '55vh' }}
              >
                {requiredAgreements.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p>כל ההסכמים חתומים</p>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-gray-600 leading-relaxed text-sm">
                    {allContent.split('\n').map((line, i) => {
                      if (line.startsWith('# ')) {
                        return (
                          <h2 key={i} className="text-lg font-bold text-gray-900 mt-6 mb-3 first:mt-0">
                            {line.replace('# ', '')}
                          </h2>
                        );
                      }
                      if (line.startsWith('## ')) {
                        return (
                          <h3 key={i} className="text-base font-semibold text-gray-800 mt-5 mb-2">
                            {line.replace('## ', '')}
                          </h3>
                        );
                      }
                      if (line === '---') {
                        return <hr key={i} className="my-6 border-gray-200" />;
                      }
                      if (line.startsWith('- ')) {
                        return (
                          <li key={i} className="mr-4 text-gray-600">
                            {line.replace('- ', '')}
                          </li>
                        );
                      }
                      if (line.trim() === '') {
                        return <br key={i} />;
                      }
                      return <p key={i} className="mb-1">{line}</p>;
                    })}
                  </div>
                )}
              </div>

              {/* Scroll hint */}
              {!scrolledToBottom && requiredAgreements.length > 0 && (
                <div className="text-center py-2 bg-gradient-to-t from-white to-transparent -mt-8 relative z-10">
                  <span className="text-xs text-gray-400 animate-bounce inline-block">
                    ↓ גלול למטה לקריאת ההסכם המלא ↓
                  </span>
                </div>
              )}

              {/* Separator */}
              <div className="border-t border-gray-100" />

              {/* Acceptance + Sign */}
              <div className="p-6 space-y-4">
                {error && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {requiredAgreements.length > 0 && (
                  <>
                    <label className={`
                      flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2 transition-all
                      ${accepted
                        ? 'border-teal-400 bg-teal-50/50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}>
                      <input
                        type="checkbox"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        className="w-5 h-5 accent-teal-600 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        קראתי ואני מסכים/ה לתנאי השימוש ולמדיניות הפרטיות
                      </span>
                    </label>

                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSign}
                      disabled={!accepted || signing}
                      className={`
                        w-full py-3 rounded-xl font-medium text-white flex items-center justify-center gap-2 transition-all
                        ${accepted && !signing
                          ? 'bg-teal-600 hover:bg-teal-700 shadow-sm'
                          : 'bg-gray-300 cursor-not-allowed'
                        }
                      `}
                    >
                      {signing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          חותם...
                        </>
                      ) : (
                        <>
                          <FileCheck className="w-5 h-5" />
                          אישור וחתימה
                        </>
                      )}
                    </motion.button>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className="w-full py-2 text-gray-400 hover:text-gray-600 text-sm flex items-center justify-center gap-1 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  יציאה מהמערכת
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Version info */}
        {requiredAgreements.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-4">
            גרסה {requiredAgreements[0]?.version}
          </p>
        )}
      </div>
    </div>
  );
}
